import { DashboardModal } from "@dashboard/components/Modal";
import { OrderGrantedRefundStatusEnum, useOrderDetailsWithMetadataQuery } from "@dashboard/graphql";
import { useHasManageProductsPermission } from "@dashboard/orders/hooks/useHasManageProductsPermission";
import { pollGrantedRefundStatus } from "@dashboard/orders/utils/refundConfirmation";
import { Box, Spinner, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage } from "react-intl";

import { refundProcessingMessages } from "./messages";

export interface RefundProcessingDialogProps {
  open: boolean;
  orderId: string;
  refundId: string | undefined;
  onResolved: (status: OrderGrantedRefundStatusEnum) => void;
}

/**
 * A refund was just requested and needs the gateway's confirmation before it's
 * safe to prompt the merchant to cancel the order (see refundConfirmation.ts).
 * This dialog blocks that window instead of leaving the merchant on a page with
 * no feedback, and refetches the same order query the page itself renders from,
 * so the payment summary/pill update without a manual reload.
 */
export const RefundProcessingDialog = ({
  open,
  orderId,
  refundId,
  onResolved,
}: RefundProcessingDialogProps) => {
  const hasManageProducts = useHasManageProductsPermission();
  const { refetch } = useOrderDetailsWithMetadataQuery({
    variables: { id: orderId, hasManageProducts },
    skip: !open || !refundId,
  });

  React.useEffect(() => {
    if (!open || !refundId) {
      return;
    }

    let cancelled = false;

    pollGrantedRefundStatus({
      refundId,
      refetch,
      isCancelled: () => cancelled,
    }).then(status => {
      if (!cancelled) {
        onResolved(status);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refundId]);

  return (
    <DashboardModal open={open}>
      <DashboardModal.Content size="sm">
        <Box display="flex" flexDirection="column" alignItems="center" gap={4} padding={6}>
          <Spinner />
          <Text size={5} fontWeight="bold">
            <FormattedMessage {...refundProcessingMessages.dialogTitle} />
          </Text>
          <Text color="default2" textAlign="center">
            <FormattedMessage {...refundProcessingMessages.dialogContent} />
          </Text>
        </Box>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

RefundProcessingDialog.displayName = "RefundProcessingDialog";
export default RefundProcessingDialog;
